import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { portfolioService } from '../../services/portfolio';
import { categoriesService } from '../../services/categories';
import type { Category } from '../../services/categories';
import type { PortfolioItem } from '../../services/portfolio';
import { getApiOrigin } from '../../lib/api-url';
import { getMediaAlt, type PortfolioMediaTile } from '../../lib/portfolio-tiles';
import {
  filterPortfolioItemsForSeoPath,
  getGalleryFilterCategoryId,
  getPortfolioCtaHref,
  type PortfolioSeoPath,
} from '../../seo/portfolio-category-map';
import PortfolioNetflixGallery from './PortfolioNetflixGallery';

const imgSrc = (url?: string) => (!url ? '' : url.startsWith('http') ? url : `${getApiOrigin()}${url}`);

type Props = {
  seoPath: PortfolioSeoPath;
  galleryTitle?: string;
  onCtaResolved?: (href: string) => void;
};

export function PortfolioSeoSection({ seoPath, galleryTitle, onCtaResolved }: Props) {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([portfolioService.getAll(), categoriesService.getAll()])
      .then(([portfolioData, cats]) => {
        setItems(portfolioData);
        setCategories(cats);
      })
      .catch(() => setLoadError('Impossible de charger la galerie pour le moment.'))
      .finally(() => setLoading(false));
  }, [seoPath]);

  const filteredItems = useMemo(
    () => filterPortfolioItemsForSeoPath(items, categories, seoPath),
    [items, categories, seoPath],
  );

  const filterCategoryId = useMemo(
    () => getGalleryFilterCategoryId(items, categories, seoPath),
    [items, categories, seoPath],
  );

  const ctaHref = useMemo(
    () => getPortfolioCtaHref(items, categories, seoPath),
    [items, categories, seoPath],
  );

  useEffect(() => {
    if (!loading && onCtaResolved) onCtaResolved(ctaHref);
  }, [ctaHref, loading, onCtaResolved]);

  const openTile = (tile: PortfolioMediaTile) => {
    if (tile.kind === 'video' && tile.videoUrl) {
      window.open(tile.videoUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setLightbox({ url: tile.imageUrl, alt: getMediaAlt(tile) });
  };

  return (
    <section className="border-t border-gray-100 bg-zinc-950 py-16 text-white md:py-20">
      <div className="mb-8 px-4 text-center sm:px-6 lg:px-8">
        <span className="text-sm font-bold uppercase tracking-wider text-primary-400">Galerie</span>
        <h2 className="mt-2 text-3xl font-black tracking-tighter md:text-4xl">
          {galleryTitle ?? 'Nos réalisations en images'}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-zinc-400">
          Sélection de nos projets — cliquez pour agrandir ou découvrir la galerie complète.
        </p>
      </div>

      {loading && (
        <div className="flex flex-col items-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
          <p className="mt-4 text-xs font-bold uppercase tracking-widest text-zinc-500">
            Chargement de la galerie...
          </p>
        </div>
      )}

      {!loading && loadError && (
        <p className="px-4 text-center text-zinc-400">{loadError}</p>
      )}

      {!loading && !loadError && filteredItems.length > 0 && (
        <>
          <PortfolioNetflixGallery
            items={filteredItems}
            categories={categories}
            imgSrc={imgSrc}
            onOpenTile={openTile}
            filterCategoryId={filterCategoryId}
            showHero={false}
            compact
            maxRows={filterCategoryId ? undefined : 4}
          />
          <div className="mt-10 text-center">
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-8 py-3 text-sm font-bold text-primary-400 transition hover:bg-primary-500 hover:text-white"
            >
              Voir toute la galerie →
            </Link>
          </div>
        </>
      )}

      {!loading && !loadError && filteredItems.length === 0 && (
        <div className="py-12 text-center text-zinc-500">
          <p className="text-sm font-bold uppercase tracking-widest">Galerie bientôt disponible</p>
          <Link
            to="/portfolio"
            className="mt-6 inline-flex rounded-full border border-white/20 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
          >
            Explorer le portfolio complet
          </Link>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Fermer"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={imgSrc(lightbox.url)}
            alt={lightbox.alt}
            draggable={false}
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )}
    </section>
  );
}
